/**
 * Lógica da sidebar de chat: filtros, etapas, contadores e scroll.
 */
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { ChatConversation } from './types';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useCrmData } from '../../../contexts/CrmProviders';
import { useChatSidebarFilters } from './useChatSidebarFilters';
import { useChatFavorites } from './useChatFavorites';
import { cleanPhone, comparePhones } from '../../../utils/phoneUtils';
import { supabase } from '../../../lib/supabase';

const MAX_AUTO_LOAD_ATTEMPTS = 5;
const AUTO_LOAD_LIST_MIN = 10;
const LOAD_MORE_PX = 200;

interface UseChatSidebarLogicProps {
    conversations: ChatConversation[];
    currentUserId?: string | null;
    onLoadMore?: () => void;
    isLoadingMore?: boolean;
    hasMoreConversations?: boolean;
    backendCounters?: { unassigned: number; mine: number; all: number };
}

export function useChatSidebarLogic({
    conversations,
    currentUserId,
    onLoadMore,
    isLoadingMore = false,
    hasMoreConversations = true,
    backendCounters,
}: UseChatSidebarLogicProps) {
    const { leads, funnelStages, crmUsers } = useCrmData();
    const { currentOrganization } = useOrganization();
    const { favoriteIds, isFavorite } = useChatFavorites();

    const [stageNamesById, setStageNamesById] = useState<Record<string, string>>({});
    const [isNewChatModalOpen, setIsNewChatModalOpen] = useState(false);

    const userRole = currentOrganization?.role || 'member';

    useEffect(() => {
        if (!currentOrganization?.id) {
            setStageNamesById({});
            return;
        }
        let cancelled = false;
        void (async () => {
            const { data, error } = await supabase
                .from('funnel_stages')
                .select('id, name')
                .eq('organization_id', currentOrganization.id);

            if (cancelled || error) return;

            const map: Record<string, string> = {};
            for (const row of data || []) {
                map[row.id] = row.name;
            }
            setStageNamesById(map);
        })();
        return () => {
            cancelled = true;
        };
    }, [currentOrganization?.id]);

    const filterState = useChatSidebarFilters(
        conversations,
        currentUserId,
        favoriteIds,
    );

    const resolveConversationStageName = useCallback(
        (conv: ChatConversation): string | null => {
            const phone = cleanPhone(conv.id);
            const openLead = leads.find((l) => {
                if (!comparePhones(l.whatsApp || '', phone)) return false;
                return (l.status || 'open') === 'open';
            });

            if (openLead?.stage) {
                return (
                    stageNamesById[openLead.stage]
                    ?? funnelStages.find((s) => s.id === openLead.stage)?.name
                    ?? conv.stageName
                    ?? null
                );
            }

            return conv.stageName ?? null;
        },
        [leads, funnelStages, stageNamesById],
    );

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        if (!onLoadMore || isLoadingMore || !hasMoreConversations) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < LOAD_MORE_PX) {
            onLoadMore();
        }
    }, [onLoadMore, isLoadingMore, hasMoreConversations]);

    const autoLoadAttemptsRef = useRef(0);
    const lastFilterKeyRef = useRef('');
    const filterKey = [
        filterState.activeTab,
        filterState.showUnreadOnly,
        filterState.showArchived,
        filterState.searchTerm,
        filterState.assignedUserIds.join(','),
        filterState.channelConnectionId ?? '',
    ].join('|');

    useEffect(() => {
        if (filterKey !== lastFilterKeyRef.current) {
            autoLoadAttemptsRef.current = 0;
            lastFilterKeyRef.current = filterKey;
        }
        if (!onLoadMore || isLoadingMore || !hasMoreConversations) return;
        if (
            filterState.filteredConversations.length < AUTO_LOAD_LIST_MIN
            && autoLoadAttemptsRef.current < MAX_AUTO_LOAD_ATTEMPTS
        ) {
            autoLoadAttemptsRef.current += 1;
            const timer = setTimeout(() => onLoadMore(), 150);
            return () => clearTimeout(timer);
        }
    }, [
        filterKey,
        filterState.filteredConversations.length,
        conversations.length,
        onLoadMore,
        isLoadingMore,
        hasMoreConversations,
    ]);

    // Tabs unassigned/mine/all: query org-wide (useConversationCounters + cache).
    // Favoritos: só existem na lista carregada + favoriteIds.
    const counters = useMemo(() => {
        const favorites = conversations.reduce((sum, c) => (
            c.status !== 'closed' && favoriteIds.has(c.id) ? sum + (c.unreadCount ?? 0) : sum
        ), 0);
        return {
            unassigned: backendCounters?.unassigned ?? 0,
            mine: backendCounters?.mine ?? 0,
            all: backendCounters?.all ?? 0,
            favorites,
        };
    }, [backendCounters, conversations, favoriteIds]);

    const getAssignedUser = useCallback(
        (assignedTo: string | undefined) => crmUsers.find((u) => u.id === assignedTo),
        [crmUsers],
    );

    return {
        isNewChatModalOpen,
        setIsNewChatModalOpen,
        userRole,
        ...filterState,
        counters,
        handleScroll,
        resolveConversationStageName,
        getAssignedUser,
        isFavorite,
        crmUsers,
    };
}
